"use client";

import React, { useState, useEffect, useRef } from 'react';

// Theme-15 color tokens
const ACCENT = '#F5C542';
const TEXT_PRIMARY = '#2D3E2D';

const BackToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollPct, setScrollPct] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [isPressed, setIsPressed] = useState(false);
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        const onScroll = () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                const scrolled = window.scrollY;
                const total = document.documentElement.scrollHeight - window.innerHeight;
                setScrollPct(total > 0 ? Math.min((scrolled / total) * 100, 100) : 0);
                setIsVisible(scrolled > 320);
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    const scrollToTop = () => {
        setIsPressed(true);
        setTimeout(() => setIsPressed(false), 200);

        // Custom eased scroll animation (easeInOutQuad)
        const start = window.scrollY;
        const duration = 1200;
        let startTime: number | null = null;

        const easeInOutQuad = (t: number) =>
            t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;

        const step = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            window.scrollTo(0, start * (1 - easeInOutQuad(progress)));
            if (progress < 1) requestAnimationFrame(step);
        };

        requestAnimationFrame(step);
    };

    // Progress circle calculations
    const size = 56;
    const strokeWidth = 3;
    const radius = (size - strokeWidth * 2) / 2;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference - (scrollPct / 100) * circumference;

    return (
        <button
            onClick={scrollToTop}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-label="Back to top"
            className="fixed bottom-24 right-6 z-[9999] focus:outline-none"
            style={{
                width: `${size}px`,
                height: `${size}px`,
                opacity: isVisible ? 1 : 0,
                transform: isVisible
                    ? isPressed
                        ? 'translateY(0) scale(0.95)'
                        : isHovered
                            ? 'translateY(-2px) scale(1.05)'
                            : 'translateY(0) scale(1)'
                    : 'translateY(16px) scale(0.9)',
                pointerEvents: isVisible ? 'auto' : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
        >
            {/* Outer glow effect */}
            <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                    background: isHovered
                        ? `radial-gradient(circle, ${ACCENT}26 0%, transparent 70%)`
                        : 'transparent',
                    transform: 'scale(1.8)',
                    transition: 'background 0.3s ease',
                }}
            />

            {/* Progress ring SVG */}
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                className="absolute inset-0"
                style={{ transform: 'rotate(-90deg)' }}
            >
                {/* Background track */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={`${ACCENT}1A`}
                    strokeWidth={strokeWidth}
                />
                {/* Progress circle */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={ACCENT}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                    style={{
                        transition: 'stroke-dashoffset 0.2s ease',
                        filter: isHovered ? `drop-shadow(0 0 6px ${ACCENT}66)` : 'none',
                    }}
                />
            </svg>

            {/* Main button */}
            <div
                className="absolute inset-1 rounded-full flex items-center justify-center transition-all duration-300"
                style={{
                    background: isHovered
                        ? `linear-gradient(135deg, ${ACCENT} 0%, #e6b030 100%)`
                        : '#FFFFFF',
                    boxShadow: isHovered
                        ? `0 8px 25px ${ACCENT}4D, 0 3px 10px rgba(0,0,0,0.1)`
                        : '0 4px 15px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.05)',
                    border: `2px solid ${isHovered ? ACCENT : `${ACCENT}33`}`,
                }}
            >
                {/* Arrow icon — dual icon slides up on hover */}
                <div className="relative overflow-hidden h-[18px] w-[18px]">
                    <div
                        className="flex flex-col"
                        style={{
                            transform: isHovered ? 'translateY(-19px)' : 'translateY(0)',
                            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {/* First arrow — shown by default */}
                        <svg
                            width="18"
                            height="18"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke={isHovered ? '#FFFFFF' : TEXT_PRIMARY}
                            strokeWidth={2.5}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                        </svg>
                        {/* Second arrow — slides in on hover */}
                        <svg
                            width="18"
                            height="18"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="#FFFFFF"
                            strokeWidth={2.5}
                            style={{ marginTop: '1px' }}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Tooltip */}
            <div
                className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap pointer-events-none"
                style={{
                    opacity: isHovered ? 1 : 0,
                    transform: isHovered
                        ? 'translateY(-50%) translateX(0)'
                        : 'translateY(-50%) translateX(8px)',
                    transition: 'all 0.3s ease',
                }}
            >
                <div
                    className="text-xs font-semibold px-3 py-2 rounded-lg shadow-lg relative"
                    style={{ background: TEXT_PRIMARY, color: '#FAF7F2' }}
                >
                    Back to top
                    {/* Arrow pointing to button */}
                    <div
                        className="absolute left-full top-1/2 -translate-y-1/2 w-0 h-0"
                        style={{
                            borderLeft: `4px solid ${TEXT_PRIMARY}`,
                            borderTop: '4px solid transparent',
                            borderBottom: '4px solid transparent',
                        }}
                    />
                </div>
            </div>

            {/* Ripple effect on click */}
            {isPressed && (
                <div
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                        background: `radial-gradient(circle, ${ACCENT}4D 0%, transparent 70%)`,
                        animation: 'ripple15 0.6s ease-out',
                    }}
                />
            )}

            <style>{`
                @keyframes ripple15 {
                    0% { transform: scale(0); opacity: 1; }
                    100% { transform: scale(2); opacity: 0; }
                }
            `}</style>
        </button>
    );
};

export default BackToTopButton;
